import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Clock, Bookmark, Trash2, ArrowUpRight, FileText } from "lucide-react";
import { fmtRelative } from "../lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ReadLaterPage() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["read-later"],
    queryFn: () => axios.get(`${API}/api/read-later`, { withCredentials: true }).then((r) => r.data),
  });
  const items = data?.items || [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["read-later"] });

  const promote = useMutation({
    mutationFn: (id) => axios.post(`${API}/api/read-later/${id}/promote`, {}, { withCredentials: true }),
    onSuccess: () => {
      toast.success("PROMOTED_TO_LIBRARY — now permanent", {
        action: { label: "OPEN_LIBRARY", onClick: () => (window.location.href = "/library") },
      });
      refresh();
      qc.invalidateQueries({ queryKey: ["saves"] });
    },
    onError: (e) => toast.error("PROMOTE_FAILED", { description: e?.response?.data?.detail || "" }),
  });
  const remove = useMutation({
    mutationFn: (id) => axios.delete(`${API}/api/read-later/${id}`, { withCredentials: true }),
    onSuccess: () => { toast.success("REMOVED_FROM_QUEUE"); refresh(); },
    onError: () => toast.error("REMOVE_FAILED"),
  });
  const clearAll = useMutation({
    mutationFn: () => axios.delete(`${API}/api/read-later`, { withCredentials: true }),
    onSuccess: (r) => { toast.success(`QUEUE_CLEARED · ${r.data?.cleared || 0} item(s)`); refresh(); },
    onError: () => toast.error("CLEAR_FAILED"),
  });

  return (
    <div data-testid="read-later-page" className="p-4 space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-mono font-bold tracking-tight text-white flex items-center gap-2">
            <Clock size={18} className="text-purple-300" /> SAVE_FOR_LATER · READING_QUEUE
          </h1>
          <p className="mt-1 text-[12px] text-zinc-400 max-w-2xl leading-relaxed">
            A <span className="text-purple-300">temporary holding space</span> for synthesis outputs you
            want to revisit — <span className="text-white">not permanent storage</span>. Promote anything
            worth keeping to your <Link to="/library" className="text-amber-300 underline underline-offset-2">Research Library</Link>,
            or remove it when you're done. The queue persists across sessions.
          </p>
        </div>
        <button
          data-testid="clear-queue-btn"
          onClick={() => { if (window.confirm(`Clear the entire reading queue (${items.length} item(s))?`)) clearAll.mutate(); }}
          disabled={items.length === 0 || clearAll.isPending}
          className="inline-flex items-center gap-1.5 px-3 h-8 text-[10px] font-mono uppercase tracking-wider border border-rose-400/60 text-rose-300 hover:bg-rose-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Trash2 size={11} /> CLEAR_QUEUE
        </button>
      </div>

      <section className="border border-[#27272A]">
        {items.length === 0 ? (
          <div data-testid="read-later-empty" className="grid-bg p-10 text-center">
            <Clock size={26} className="mx-auto text-purple-400" />
            <div className="mt-2 text-[12px] font-mono uppercase tracking-wider text-zinc-500">
              QUEUE_EMPTY — hit "Save for Later" under any bot synthesis to park it here
            </div>
            <Link to="/bot" className="mt-3 inline-block px-4 h-9 leading-9 bg-amber-400 text-black font-mono uppercase text-[11px] tracking-wider hover:bg-amber-300">
              OPEN_THE_BOT →
            </Link>
          </div>
        ) : (
          items.map((it) => (
            <div key={it.id} data-testid={`read-later-item-${it.id}`}
                 className="border-b border-[#161616] p-3 hover:bg-[#0E0E0E] flex flex-col md:flex-row gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[13.5px] text-white font-mono">{it.topic || "(untitled synthesis)"}</span>
                  <span className="text-[10px] font-mono text-zinc-600">{fmtRelative(it.created_at)}</span>
                  {it.exec_summary?.pdf_url && (
                    <a href={`${API}${it.exec_summary.pdf_url}`} target="_blank" rel="noopener noreferrer"
                       data-testid={`queue-pdf-${it.id}`}
                       className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-amber-300 hover:text-amber-200">
                      <FileText size={10} /> PDF
                    </a>
                  )}
                </div>
                {it.preview && (
                  <p className="mt-1 text-[12px] text-zinc-400 leading-snug line-clamp-3">
                    <span className="text-emerald-400 font-mono text-[10px] uppercase mr-1">GROUNDED_CONCLUSION ·</span>
                    {it.preview}
                  </p>
                )}
              </div>
              <div className="flex md:flex-col gap-1.5 shrink-0">
                <button
                  data-testid={`promote-${it.id}`}
                  onClick={() => promote.mutate(it.id)}
                  disabled={promote.isPending}
                  title="Promote to the permanent Research Library"
                  className="inline-flex items-center gap-1 px-2.5 h-7 text-[10px] font-mono uppercase tracking-wider border border-amber-400/60 text-amber-300 hover:bg-amber-500/10 disabled:opacity-50"
                >
                  <Bookmark size={10} /> PROMOTE_TO_LIBRARY <ArrowUpRight size={10} />
                </button>
                <button
                  data-testid={`remove-${it.id}`}
                  onClick={() => remove.mutate(it.id)}
                  disabled={remove.isPending}
                  className="inline-flex items-center gap-1 px-2.5 h-7 text-[10px] font-mono uppercase tracking-wider border border-[#27272A] text-zinc-400 hover:text-rose-300 hover:border-rose-400/60 disabled:opacity-50"
                >
                  <Trash2 size={10} /> REMOVE
                </button>
              </div>
            </div>
          ))
        )}
      </section>
    </div>
  );
}
